import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PageHeader,
  DashboardCard,
  FilterBar,
  StatusBadge,
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@zafroi/ui-kit';
import { useAccounts, useKoshLabels } from '@/hooks/useKosh';
import { QueryStateGuard } from '@/components/common/QueryStateGuard';
import { accountStatusVariants } from '@/config/kosh-status';
import { shortDate } from '@/lib/format';

export function CustomersPage() {
  const navigate = useNavigate();
  const q = useAccounts();
  const { labelPlural } = useKoshLabels();
  const [search, setSearch] = useState('');

  const accounts = q.data ?? [];
  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return accounts;
    return accounts.filter(
      (a) =>
        a.name.toLowerCase().includes(s) ||
        a.code.toLowerCase().includes(s) ||
        (a.industry ?? '').toLowerCase().includes(s),
    );
  }, [accounts, search]);

  const noun = labelPlural('customer');

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Commercial"
        title={noun}
        description={`Master of your ${noun.toLowerCase()}, accounts and contacts.`}
      />
      <DashboardCard flushBody>
        <div className="border-b border-border p-3">
          <FilterBar
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder={`Search ${noun.toLowerCase()}…`}
          />
        </div>
        <QueryStateGuard
          data={filtered}
          isLoading={q.isLoading}
          isError={q.isError}
          emptyTitle={`No ${noun.toLowerCase()} yet`}
        >
          {(rows) => (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Industry</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Added</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((a) => (
                  <TableRow
                    key={a.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/customers/${a.id}`)}
                  >
                    <TableCell className="font-mono text-[12px]">{a.code}</TableCell>
                    <TableCell className="font-medium">{a.name}</TableCell>
                    <TableCell className="text-muted-foreground">{a.industry ?? '—'}</TableCell>
                    <TableCell>
                      <StatusBadge status={a.status} variants={accountStatusVariants} />
                    </TableCell>
                    <TableCell className="text-muted-foreground">{shortDate(a.createdAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </QueryStateGuard>
      </DashboardCard>
    </div>
  );
}
